import { useState, useCallback, useRef, useEffect } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'
import { QRCodeRegenerationStatus } from '@/utils/qr-code/qr-code-regeneration.constant'

interface RegenerateResponse {
  process_id: string
  status: string
  message: string
}

interface CheckStatusResponse {
  status: string
  message?: string
}

interface UseRegenerateErrorQRCodesParams {
  onInProgress?: () => void
  onDone?: () => Promise<void>
}

interface UseRegenerateErrorQRCodesReturn {
  regenerateErrorQRCodes: () => Promise<void>
  startPollingByProcessId: (processId: string) => void
  isRegeneratingErrors: boolean
  error: Nullable<string>
}

const POLLING_INTERVAL = 5000

export function useRegenerateErrorQRCodes(
  params?: UseRegenerateErrorQRCodesParams
): UseRegenerateErrorQRCodesReturn {
  const [isRegeneratingErrors, setIsRegeneratingErrors] = useState(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)
  const shouldContinuePollingRef = useRef<boolean>(false)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const checkStatus = useCallback(
    async (processId: string): Promise<string> => {
      try {
        const response = await apiClient.get<CheckStatusResponse>(
          '/qr-codes/check-status-regenerate',
          {
            params: {
              process_id: processId,
              wait_seconds: POLLING_INTERVAL / 1000,
            },
          }
        )
        return response.data.status
      } catch (err) {
        setError(handleErrorRef.current(err))
        return ''
      }
    },
    []
  )

  const pollStatus = useCallback(
    async (processId: string): Promise<void> => {
      if (!shouldContinuePollingRef.current) {
        return
      }

      const status = await checkStatus(processId)
      const normalizedStatus = status.toLowerCase()

      if (normalizedStatus === QRCodeRegenerationStatus.DONE) {
        shouldContinuePollingRef.current = false
        setIsRegeneratingErrors(false)
        if (params?.onDone) {
          await params.onDone()
        }
      } else if (normalizedStatus === QRCodeRegenerationStatus.IN_PROGRESS) {
        if (shouldContinuePollingRef.current) {
          await pollStatus(processId)
        }
      } else {
        shouldContinuePollingRef.current = false
        setIsRegeneratingErrors(false)
      }
    },
    [checkStatus, params?.onDone]
  )

  const startPolling = useCallback(
    (processId: string) => {
      shouldContinuePollingRef.current = true
      pollStatus(processId)
    },
    [pollStatus]
  )

  const regenerateErrorQRCodes = async () => {
    setIsRegeneratingErrors(true)
    setError(null)
    shouldContinuePollingRef.current = false

    try {
      const response = await apiClient.post<RegenerateResponse>(
        '/qr-codes/regenerate-errors'
      )
      const { process_id, status } = response.data
      const normalizedStatus = status.toLowerCase()

      if (
        normalizedStatus === QRCodeRegenerationStatus.IN_PROGRESS &&
        process_id
      ) {
        if (params?.onInProgress) {
          params.onInProgress()
        }
        shouldContinuePollingRef.current = true
        await pollStatus(process_id)
      } else if (normalizedStatus === QRCodeRegenerationStatus.DONE) {
        setIsRegeneratingErrors(false)
        if (params?.onDone) {
          await params.onDone()
        }
      }
    } catch (err) {
      setError(handleErrorRef.current(err))
      setIsRegeneratingErrors(false)
      shouldContinuePollingRef.current = false
      throw err
    }
  }

  return {
    regenerateErrorQRCodes,
    startPollingByProcessId: startPolling,
    isRegeneratingErrors,
    error,
  }
}
